import { DELIVERY_ORDER_ACCEPT } from './delivery-order-upload.mjs?v=20260806.100';
import { workspaceChoiceMarkup } from './workspace-choice-control.mjs?v=20260806.100';
import { CUSTOM_ADDRESS_VALUE } from './address-catalog.mjs?v=20260806.100';
import {
  CONTAINER_FIELD_REGISTRY,
  columnEditableForRole,
  fieldOptions,
} from './container-field-registry.mjs?v=20260806.100';
import { containerStatusLabel } from './container-status-presentation.mjs?v=20260806.100';
import { parseBulkValue } from './bulk-value.mjs?v=20260806.100';
import { weightToPounds } from './weight-unit.mjs?v=20260806.100';

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

/**
 * One field: a caption notched into the box's own top border, and a box that is
 * empty until someone fills it in.
 *
 * There is no placeholder option on purpose (Alan, 2026-08-08). A placeholder is
 * text sitting inside an empty control that disappears the moment it is needed,
 * and on this form it was doing the caption's job twice over - the caption is on
 * the border where it stays readable while you type.
 */
// What goes to the callable is always pounds. A pound figure is passed as typed; a kilogram
// one is converted, and a number that will not convert is left for the callable to refuse.
function weightInPounds(values) {
  const typed = optional(values.weight);
  if (String(values.weight_unit ?? '').toLowerCase() !== 'kg') return typed;
  const converted = weightToPounds(typed, 'kg');
  return converted.error ? typed : String(converted.value);
}

function field(label, name, options = {}) {
  const type = options.type || 'text';
  const required = options.required === false ? '' : ' required';
  const inputMode = options.inputmode ? ` inputmode="${escapeHtml(options.inputmode)}"` : '';
  const autocomplete = options.autocomplete ? ` autocomplete="${escapeHtml(options.autocomplete)}"` : '';
  const wide = options.wide ? ' create-record-field--wide' : '';
  const control = options.multiline
    ? `<textarea name="${escapeHtml(name)}" maxlength="${options.maxlength || 500}"${required}></textarea>`
    : `<input name="${escapeHtml(name)}" type="${escapeHtml(type)}" maxlength="${options.maxlength || 200}"${inputMode}${autocomplete}${required}>`;
  return `<label class="create-record-field${wide}"><span>${escapeHtml(label)}</span>${control}<small data-field-error></small></label>`;
}

const SIZE_CHOICES = [
  { value: '20', label: '20', tone: 'available' },
  { value: '40HQ', label: '40HQ', tone: 'available' },
  { value: '45', label: '45', tone: 'available' },
  { value: 'Custom', label: 'Custom', tone: 'neutral' },
];

const FORECAST_CHOICES = [
  { value: 'Pre-Alert', label: 'Pre-Alert', tone: 'paid' },
  { value: 'Pre-Alert + DO', label: 'Pre-Alert + DO', tone: 'paid' },
];
// Customer Chassis asks "does the CUSTOMER bring their own chassis?" and reads Yes /
// No (Alan, 2026-08-02). The stored token is not the answer to that question - it
// records what WE did, a convention pinned against production on 2026-08-01 (all 68
// containers storing "yes" carry a chassis number, all 342 storing "no" carry none):
//
//   'own_chassis' -> we assigned nothing, the customer brought one -> reads Yes
//   'yes'         -> we assigned a chassis, so the customer had none -> reads No
//
// So the No option must store 'yes'. It looks inverted here because it is: the label
// answers about the customer, the value records what we did. This form used to store
// 'no' for the No option, and customerSuppliesOwnChassis() treats every value except
// 'yes' as the customer's own - so BOTH options came out reading Yes, and picking No
// silently produced a container that says the customer brought a chassis.
//
// 'own_chassis' is load-bearing beyond the label: app.js gates the empty-container
// input on that exact token. Do not collapse it into 'no'.
// Yes stores "yes" (Alan, 2026-08-10). It used to store "own_chassis" for Yes and
// "yes" for No, because the token recorded what WE did rather than what the
// customer has - so the form's own label and the value under it disagreed, and
// every reader had to invert it. All 498 rows were cleared to blank before this
// changed, so nothing stored still carries the old meaning.
export const CUSTOMER_CHASSIS_CHOICES = [
  { value: 'yes', label: 'Yes', tone: 'pending' },
  { value: 'no', label: 'No', tone: 'neutral' },
];

/**
 * The price the chosen address was quoted at, shown under the picker. Staff see
 * the same card the customer does (Alan, 2026-08-08) - whoever is filing the task
 * is agreeing to a price, and reading it off a separate screen is how the wrong
 * one gets agreed to. It stays hidden until an address carrying a quote is
 * chosen, so Custom and the empty prompt show nothing rather than an empty card.
 *
 * A role that does not own pricing is served the same addresses with the money
 * blanked by the server (pickerCustomerQuoteLocationView), so this card simply
 * does not appear for them. That gate is on the callable, not here.
 */
function quoteCardMarkup() {
  return `<section class="create-record-quote create-record-field--wide" data-customer-quote-card hidden aria-live="polite">
      <span>Customer Quote</span>
      <output data-customer-quote-output></output>
      <small data-customer-quote-terms></small>
    </section>`;
}

function mountQuoteCard(root) {
  const card = root?.querySelector?.('[data-customer-quote-card]');
  const output = root?.querySelector?.('[data-customer-quote-output]');
  const terms = root?.querySelector?.('[data-customer-quote-terms]');
  if (!card || !output || !terms) return () => {};
  return (option) => {
    const quote = String(option?.dataset?.quote ?? '');
    const quoteTerms = String(option?.dataset?.terms ?? '');
    output.textContent = quote;
    terms.textContent = quoteTerms;
    card.hidden = !quote && !quoteTerms;
  };
}

function quoteDataAttributes(location) {
  return `data-quote="${escapeHtml(location?.quote_display || '')}"`
    + ` data-terms="${escapeHtml(location?.quote_terms || '')}"`;
}

function customerQuoteLocationMarkup(quoteLocations) {
  const options = quoteLocations.map((location) => `
    <option value="${escapeHtml(location.id)}"
      data-revision="${escapeHtml(location.revision)}"
      ${quoteDataAttributes(location)}>${escapeHtml(location.selection_label)}</option>`).join('');
  return `<label class="create-record-field create-record-field--wide"><span>Delivery Address</span>
      <select name="quote_location_id">
        <option value="">${options ? 'Choose an approved address' : 'No approved addresses available'}</option>
        ${options}
      </select>
      <input name="quote_location_revision" type="hidden" value="">
      <small data-field-error></small>
    </label>
    ${quoteCardMarkup()}`;
}

/**
 * Delivery Address is a choice for everyone now, not only the customer
 * (Alan, 2026-08-02). Internal roles typed it free-hand, so the same warehouse
 * arrived spelled three ways and matched neither the customer's approved list nor
 * the address their quote was priced against.
 *
 * The options belong to whichever Customer the form is filling in for, so they are
 * loaded after that name is chosen rather than rendered here. Custom stays on the
 * end: Add Task can invent a provisional Customer, who has no approved addresses at
 * all, and that has to remain fileable.
 */
function operationsDeliveryAddressMarkup() {
  return `<label class="create-record-field create-record-field--wide"><span>Delivery Address</span>
      <select name="delivery_address_choice" data-delivery-address-select>
        ${deliveryAddressOptionsMarkup([])}
      </select>
      <small data-field-error></small>
    </label>
    <label class="create-record-field create-record-field--wide" data-delivery-address-custom-field hidden>
      <span>Custom Address</span>
      <input name="delivery_warehouse_address" data-delivery-address-custom type="text"
        maxlength="500" autocomplete="street-address">
      <small data-field-error></small>
    </label>
    ${quoteCardMarkup()}`;
}

/**
 * The prompt says what to do next rather than describing a state nobody caused.
 *
 * Three situations, and the middle one is the reason this takes a name rather
 * than a boolean. Add Task can invent a provisional Customer, whose name matches
 * no account and therefore has no approved list - and a typo does the same. Both
 * used to sit on "Choose the customer first" forever, with the name plainly
 * typed in the box above (Alan, 2026-08-08, "the dropdown options are gone").
 * Custom address is the way through and the prompt now says so.
 */
const ADDRESS_PROMPTS = Object.freeze({
  unnamed: 'Choose the customer first',
  unknown: 'No approved address for this customer — use Custom address',
  none: 'No approved address on file — use Custom address',
  some: 'Choose an approved address',
});

function deliveryAddressPrompt(approvedCount, customerState) {
  if (customerState === 'unnamed') return ADDRESS_PROMPTS.unnamed;
  if (customerState === 'unknown') return ADDRESS_PROMPTS.unknown;
  return approvedCount ? ADDRESS_PROMPTS.some : ADDRESS_PROMPTS.none;
}

function deliveryAddressOptionsMarkup(locations, { customerState = 'unnamed' } = {}) {
  const approved = (Array.isArray(locations) ? locations : [])
    .filter((location) => String(location?.address ?? '').trim());
  return `<option value="">${escapeHtml(deliveryAddressPrompt(approved.length, customerState))}</option>
    ${approved.map((location) => {
    const address = String(location.address).trim();
    return `<option value="${escapeHtml(address)}" ${quoteDataAttributes(location)}>${escapeHtml(location.selection_label || address)}</option>`;
  }).join('')}
    <option value="${escapeHtml(CUSTOM_ADDRESS_VALUE)}">Custom address</option>`;
}

export function createRecordWorkspaceMarkup({
  type,
  role,
  customers = [],
  quoteLocations = [],
} = {}) {
  if (type === 'customer') {
    return `<span data-workspace-container-number>Add Customer</span>
      <form class="create-record-workspace" data-create-record-form="customer" data-workspace-done-target novalidate>
        <div class="create-record-grid">
          ${field('Customer Name', 'name', { autocomplete: 'organization' })}
          <label class="create-record-field"><span>Account Number</span><output data-generated-account-number>Generated when saved</output></label>
          ${field('Account Login Email', 'account_login_email', { type: 'email', autocomplete: 'email' })}
          ${/*
            Optional on purpose, and it is what decides whether this form MAKES the
            login or LINKS one (Alan, 2026-08-21: "admin可以加customer 同时还可以设置
            账户和密码"). Filled: the login is created with this password first.
            Blank: the email must already be an active Customer login, which is
            what the callable has always required and the only thing this form
            could do before.

            Admin only. Dispatch may add a Customer, but handing out credentials
            is not the same permission, and the callable refuses anyone else.
          */ ''}
          ${role === 'admin' ? field('Login Password', 'account_password', {
    type: 'password',
    required: false,
    autocomplete: 'new-password',
    maxlength: 128,
  }) : ''}
          ${role === 'admin' ? `<p class="create-record-hint" data-account-password-hint>Leave blank to link an existing login. 8 characters or more creates a new one. 留空表示关联已有账号；填写 8 位以上则新建账号。</p>` : ''}
          ${/* Only the name and the login are needed (Alan, 2026-10-14: "以后不要有这种限制只要有最基础的账户和密码就行"). */ ''}
          ${field('Real Contact Email', 'real_email', { type: 'email', autocomplete: 'email', required: false })}
          ${field('Phone', 'phone', { type: 'tel', autocomplete: 'tel', required: false })}
          ${field('Address', 'address', { autocomplete: 'street-address', wide: true, required: false })}
          ${field('Delivery Notes', 'delivery_notes', { multiline: true, required: false, wide: true, maxlength: 1000 })}
        </div>
        <p class="create-record-error" data-create-record-error role="alert" aria-live="assertive"></p>
        <button type="submit" hidden>Save Customer</button>
      </form>`;
  }
  if (type === 'driver') {
    return `<span data-workspace-container-number>Add Driver</span>
      <form class="create-record-workspace" data-create-record-form="driver" data-workspace-done-target novalidate>
        <div class="create-record-grid">
          ${field('Driver Name', 'name', { autocomplete: 'name' })}
          ${field('Phone', 'phone', { type: 'tel', autocomplete: 'tel' })}
          ${field('WeChat', 'wechat', { required: false, autocomplete: 'off' })}
          ${field('Truck', 'truck', { required: false, autocomplete: 'off' })}
          ${field('Account Login Email', 'account_login_email', { type: 'email', autocomplete: 'email' })}
          ${field('Real Contact Email', 'real_email', { type: 'email', autocomplete: 'email' })}
          ${field('Note', 'note', { multiline: true, required: false, wide: true, maxlength: 1000 })}
        </div>
        <p class="create-record-error" data-create-record-error role="alert" aria-live="assertive"></p>
        <button type="submit" hidden>Save Driver</button>
      </form>`;
  }
  if (type !== 'task') throw new Error('Unsupported create workspace.');
  // Every internal role names the Customer; only the Customer themselves does not,
  // because the callable takes their identity from the token and a picker would
  // show them every other customer's name.
  const canChooseCustomer = role !== 'customer' && role !== 'driver';
  // An existing customer or a provisional one, typed. The box starts empty and the
  // caption says only what the field is: the instruction used to be a placeholder
  // inside it, which is text that vanishes exactly when it is being followed.
  const customerList = canChooseCustomer ? `<label class="create-record-field create-record-field--wide"><span>Customer</span>
      <input name="customer_name" list="createTaskCustomers" autocomplete="off" required>
      <datalist id="createTaskCustomers">${customers.map((customer) => `<option value="${escapeHtml(customer.name)}" data-customer-id="${escapeHtml(customer.id)}"></option>`).join('')}</datalist>
      <small data-field-error></small></label>` : '';
  return `<span data-workspace-container-number>New Container</span>
    <form class="create-record-workspace" data-create-record-form="task" data-workspace-done-target novalidate>
      <div class="create-record-grid">
        ${customerList}
        ${field('Container #', 'container_no', { autocomplete: 'off' })}
        ${field('MBL #', 'master_bl_no', { required: false, autocomplete: 'off' })}
        ${workspaceChoiceMarkup({ name: 'size', label: 'Container Size', options: SIZE_CHOICES })}
        ${workspaceChoiceMarkup({ name: 'pre_alert', label: 'Forecast Status', options: FORECAST_CHOICES })}
        <!-- Every account answers the chassis question, not only the customer
             (Alan, 2026-08-10: "每个账号 add task 需要有 chassis yes or no"). Whoever
             books the Task knows whether the customer is bringing their own, and
             it is what gates the Chassis # editor on the row afterwards. -->
        ${workspaceChoiceMarkup({ name: 'customer_chassis', label: 'Customer Chassis', options: CUSTOMER_CHASSIS_CHOICES })}
        <label class="create-record-field"><span>Delivery Order (DO)</span><input name="do_document" type="file" accept="${DELIVERY_ORDER_ACCEPT}"><small data-field-error></small></label>
        ${role === 'customer'
          ? customerQuoteLocationMarkup(quoteLocations)
          : operationsDeliveryAddressMarkup()}
        <!-- Containers hold pounds; kg is converted on the way in (Alan, 2026-10-01: "单个的录入do
             也需要有kg和lb转换的选项"). -->
        <label class="create-record-field create-record-field--unit"><span>Weight</span><input name="weight" type="number" step="any" min="0" inputmode="decimal" autocomplete="off"><select name="weight_unit" aria-label="Weight unit"><option value="lb">lb</option><option value="kg">kg</option></select><small data-field-error></small></label>
        ${role === 'customer' || role === 'driver' ? '' : taskExtraFieldsMarkup()}
      </div>
      <p class="create-record-error" data-create-record-error role="alert" aria-live="assertive"></p>
      <button type="submit" hidden>Save Task</button>
    </form>`;
}

// The details dispatch usually has by the time a Container is booked (Alan, 2026-09-28:
// "每次加柜子录DO需要增加，船司，码头，状态ETA LFD note"), entered on the Task
// itself instead of a second trip to the table. Every one is optional (Alan, 2026-09-30:
// "这些信息是有更好没有也不影响不要有太多的限制"): none is `required`, and none can stop the
// Container being added - they are written after it exists, and one that cannot be
// saved is named and left for the table, where every one of them stays editable.
export const TASK_EXTRA_FIELDS = Object.freeze([
  Object.freeze({ key: 'ssl', label: 'SSL' }),
  Object.freeze({ key: 'terminal_name', label: 'Terminal' }),
  Object.freeze({ key: 'ocean_status', label: 'Status' }),
  Object.freeze({ key: 'eta_at', label: 'ETA' }),
  Object.freeze({ key: 'lfd_at', label: 'LFD' }),
  Object.freeze({ key: 'hold_note', label: 'Note' }),
]);

const registryColumn = (key) => CONTAINER_FIELD_REGISTRY.find((column) => column.key === key);

function taskExtraFieldsMarkup() {
  const listed = (key) => fieldOptions(registryColumn(key) ?? {}).filter((value) => value !== 'CUSTOM');
  const datalist = (key) => `<datalist id="createTask-${key}">${listed(key).map((value) => `<option value="${escapeHtml(value)}"></option>`).join('')}</datalist>`;
  const typed = (key, label, extra = '') => `<label class="create-record-field"><span>${label}</span><input name="${key}" type="text" maxlength="200" autocomplete="off"${extra}><small data-field-error></small></label>`;
  const statuses = listed('ocean_status')
    .map((value) => `<option value="${escapeHtml(value)}">${escapeHtml(containerStatusLabel(value, 'zh'))} ${escapeHtml(containerStatusLabel(value, 'en'))}</option>`).join('');
  return `${typed('ssl', 'SSL', ' list="createTask-ssl"')}${datalist('ssl')}
        ${typed('terminal_name', 'Terminal', ' list="createTask-terminal_name"')}${datalist('terminal_name')}
        <label class="create-record-field"><span>Status</span><select name="ocean_status"><option value=""></option>${statuses}</select><small data-field-error></small></label>
        ${typed('eta_at', 'ETA')}
        ${typed('lfd_at', 'LFD')}
        <label class="create-record-field create-record-field--wide"><span>Note</span><input name="hold_note" type="text" maxlength="1000" autocomplete="off"><small data-field-error></small></label>`;
}

/**
 * The optional details as the writes they become, and the ones that cannot be.
 * Nothing here throws and nothing is required: a blank is skipped, a value this role
 * may not write or that does not read as a date/option is named in `skipped` so the
 * operator is told it was left for the table rather than silently dropped.
 */
export function taskExtraFieldWrites({ role, values, now = Date.now() } = {}) {
  const writes = [];
  const skipped = [];
  for (const { key, label } of TASK_EXTRA_FIELDS) {
    const raw = String(values?.[key] ?? '').trim();
    if (!raw) continue;
    const column = registryColumn(key);
    if (!column || !columnEditableForRole(column, role)) { skipped.push(label); continue; }
    const parsed = parseBulkValue(column, raw, { now });
    if (parsed.skip) continue;
    if (parsed.error) { skipped.push(label); continue; }
    writes.push({ field: key, value: parsed.value, label });
  }
  return { writes, skipped };
}

function required(value, label) {
  const normalized = String(value ?? '').trim();
  if (!normalized) throw new Error(`${label} is required.`);
  return normalized;
}

function optional(value) {
  return String(value ?? '').trim();
}

export function buildCreateRecordRequest(type, role, values, requestId) {
  const request_id = required(requestId, 'Request ID');
  if (type === 'customer') return {
    request_id,
    name: required(values.name, 'Customer name'),
    account_login_email: required(values.account_login_email, 'Account login email').toLowerCase(),
    real_email: optional(values.real_email).toLowerCase(),
    phone: optional(values.phone),
    address: optional(values.address),
    delivery_notes: optional(values.delivery_notes),
  };
  if (type === 'driver') return {
    request_id,
    name: required(values.name, 'Driver name'),
    phone: required(values.phone, 'Phone'),
    wechat: optional(values.wechat),
    truck: optional(values.truck),
    account_login_email: required(values.account_login_email, 'Account login email').toLowerCase(),
    real_email: required(values.real_email, 'Real contact email').toLowerCase(),
    note: optional(values.note),
  };
  if (type !== 'task') throw new Error('Unsupported create workspace.');
  const payload = {
    request_id,
    master_bl_no: optional(values.master_bl_no),
    // The DO is a file again (Alan, 2026-08-03). A link lived wherever the customer
    // happened to have put it; the document itself lives in our Storage. The
    // callable takes do_document and writes do_ref itself as the gs:// URI, and it
    // rejects the request outright if both are sent - so do_ref is not sent at all.
    ...(values.do_document ? { do_document: values.do_document } : {}),
    // The Container number is the only thing a task cannot be opened without:
    // it is the box, the document id is derived from it, and it is what a
    // duplicate is caught on. Everything else can arrive later (Alan,
    // 2026-08-07) - the size, the weight, the forecast status and whose chassis
    // it is are often three phone calls away, and refusing the task until all
    // four were in hand meant the task was never recorded at all. Absent keys
    // are omitted rather than sent empty, so nothing writes a stand-in value.
    containers: [{
      container_no: required(values.container_no, 'Container number'),
      ...(optional(values.size) ? { size: optional(values.size) } : {}),
      ...(optional(values.weight) ? { weight: weightInPounds(values) } : {}),
      ...(optional(values.pre_alert) ? { pre_alert: optional(values.pre_alert) } : {}),
      ...(optional(values.customer_chassis)
        ? { customer_chassis: optional(values.customer_chassis) } : {}),
    }],
  };
  if (role === 'customer') {
    // A quote is only sent when one was chosen. Choosing one still has to bring
    // its revision, because a stale quote prices the job wrong - that check is
    // about correctness, not about completeness, so it stays.
    if (optional(values.quote_location_id)) {
      payload.quote_location_id = optional(values.quote_location_id);
      const revision = Number(values.quote_location_revision);
      if (!Number.isSafeInteger(revision) || revision < 0) {
        throw new Error('The selected delivery quote is stale. Choose the address again.');
      }
      payload.quote_location_revision = revision;
    }
  } else if (optional(values.delivery_warehouse_address)) {
    payload.delivery_warehouse_address = optional(values.delivery_warehouse_address);
  }
  // Every internal role names the Customer, and the callable requires it from all
  // of them - createOperationsContainerTasks throws "Customer is required." before
  // it looks at anything else. This was gated on admin and dispatcher after Add
  // Task opened to clerk and finance (Alan, 2026-08-08), so those two filled the
  // whole form and the save was refused. A Customer filing their own task sends no
  // name at all: their callable takes identity from the token and rejects the key.
  if (role !== 'customer') {
    payload.customer_name = required(values.customer_name, 'Customer');
  }
  return payload;
}

function customerNameKey(value) {
  return String(value ?? '').replace(/\s+/gu, ' ').trim().toLocaleLowerCase('en');
}

/**
 * Fills the internal Delivery Address dropdown from whichever Customer the form
 * names, and keeps Custom working for the ones who have no approved address yet.
 *
 * The text field carries the saved value in both modes - picking from the list
 * copies the address into it - so the request contract is still one address string
 * and the server did not have to learn a second one. `required` moves with
 * visibility because a hidden required control cannot be focused, which would wedge
 * form validation on a field the user cannot see.
 */
export function mountOperationsDeliveryAddressControl(root, {
  customers = [],
  listLocations = async () => [],
} = {}) {
  const select = root?.querySelector?.('[data-delivery-address-select]');
  const custom = root?.querySelector?.('[data-delivery-address-custom]');
  if (!select || !custom) return { destroy() {} };
  const customField = root?.querySelector?.('[data-delivery-address-custom-field]') || custom;
  const renderQuote = mountQuoteCard(root);
  const customerInput = root?.querySelector?.('[name="customer_name"]');
  const accountUids = new Map((Array.isArray(customers) ? customers : [])
    .filter((customer) => customer?.account_uid)
    .map((customer) => [customerNameKey(customer.name), String(customer.account_uid)]));
  let generation = 0;
  let loadedUid = null;
  let renderedState = null;

  const syncCustom = () => {
    const isCustom = select.value === CUSTOM_ADDRESS_VALUE;
    customField.hidden = !isCustom;
    custom.required = isCustom;
    if (!isCustom) custom.value = select.value;
    renderQuote(isCustom ? null : select.selectedOptions?.[0]);
  };

  /**
   * Rebuilding the options clears the selection, which is what should happen:
   * an address picked for one customer is not an address for the next one.
   *
   * It lands on the prompt every time and never on Custom. It used to preselect
   * Custom whenever the list came back empty - which is the state the form opens
   * in, before any customer is named - so Add Task began with "Custom address"
   * chosen and a free-text box already open (Alan, 2026-08-08). The typing box is
   * the escape hatch, not the default: it appears when someone asks for it.
   */
  const applyLocations = (locations, customerState) => {
    const approved = (Array.isArray(locations) ? locations : [])
      .filter((location) => String(location?.address ?? '').trim());
    select.innerHTML = deliveryAddressOptionsMarkup(approved, { customerState });
    select.value = '';
    syncCustom();
  };

  /**
   * Typing runs through every prefix of a customer's name, so the approved list
   * is only refetched when the name resolves to a different account.
   *
   * The prompt has to keep up even when the account does not change, though. A
   * provisional customer - or a typo - resolves to no account at every keystroke,
   * so guarding on the uid alone left the control saying "Choose the customer
   * first" with the name plainly typed in the box above, and no hint that Custom
   * address was the way through. It tracks the rendered state as well now.
   */
  const loadForCustomer = async ({ committed = false } = {}) => {
    const name = String(customerInput?.value ?? '').trim();
    const uid = accountUids.get(customerNameKey(name)) ?? '';
    // Half a name matches nothing, so while someone is still typing an unmatched
    // name is treated as no name rather than as a provisional customer. Calling
    // it unknown on every keystroke rewrote the prompt under the cursor, which is
    // the text-jumping Alan asked to be rid of. It settles on blur.
    const nextState = uid ? 'known' : (name && committed ? 'unknown' : 'unnamed');
    if (uid === loadedUid && nextState === renderedState) return;
    loadedUid = uid;
    renderedState = nextState;
    const attempt = ++generation;
    // No account means no approved list to fetch, only a prompt to repaint.
    if (!uid) {
      applyLocations([], nextState);
      return;
    }
    let locations = [];
    try {
      locations = await listLocations(uid);
    } catch {
      locations = [];
    }
    if (attempt === generation) applyLocations(locations, nextState);
  };

  // input keeps the approved list in step as a name resolves; change and blur are
  // the commit, and only they will call a name provisional.
  const onTyping = () => loadForCustomer();
  const onCommit = () => loadForCustomer({ committed: true });
  select.addEventListener('change', syncCustom);
  customerInput?.addEventListener('input', onTyping);
  customerInput?.addEventListener('change', onCommit);
  customerInput?.addEventListener('blur', onCommit);
  applyLocations([], 'unnamed');
  loadedUid = '';
  renderedState = 'unnamed';
  return {
    destroy() {
      select.removeEventListener('change', syncCustom);
      customerInput?.removeEventListener('input', onTyping);
      customerInput?.removeEventListener('change', onCommit);
      customerInput?.removeEventListener('blur', onCommit);
    },
  };
}

export function mountCustomerQuoteLocationControl(root) {
  const select = root?.querySelector?.('[name="quote_location_id"]');
  const revision = root?.querySelector?.('[name="quote_location_revision"]');
  const card = root?.querySelector?.('[data-customer-quote-card]');
  if (!select || !revision || !card) return { destroy() {} };
  const renderQuote = mountQuoteCard(root);
  const render = () => {
    const option = select.selectedOptions?.[0];
    const selected = Boolean(select.value && option);
    revision.value = selected ? String(option.dataset.revision ?? "") : "";
    renderQuote(selected ? option : null);
  };
  select.addEventListener("change", render);
  render();
  return {
    destroy() {
      select.removeEventListener("change", render);
    },
  };
}
